import {
    ContentType,
    contentToResourceViewItem,
    ResourceViewItemType,
    spaceToResourceViewItem,
    wrapResourceView,
    type ResourceViewItem,
} from '@lightdash/common';
import { ActionIcon, Group, Stack, TextInput } from '@mantine/core';
import { IconLayoutDashboard, IconSearch, IconX } from '@tabler/icons-react';
import Fuse from 'fuse.js';
import { useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router';
import EmptyStateLoader from '../components/common/EmptyStateLoader';
import ErrorState from '../components/common/ErrorState';
import MantineIcon from '../components/common/MantineIcon';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import ResourceView from '../components/common/ResourceView';
import { ResourceSortDirection } from '../components/common/ResourceView/types';
import SuboptimalState from '../components/common/SuboptimalState/SuboptimalState';
import ForbiddenPanel from '../components/ForbiddenPanel';
import { useInfiniteContent } from '../hooks/useContent';
import { useSpace, useSpaceSummaries } from '../hooks/useSpaces';
import useApp from '../providers/App/useApp';

const MobileSpace: FC = () => {
    const { projectUuid, spaceUuid } = useParams<{
        projectUuid: string;
        spaceUuid: string;
    }>();
    const {
        data: space,
        isInitialLoading,
        error,
    } = useSpace(projectUuid, spaceUuid);
    const { data: spaces = [] } = useSpaceSummaries(projectUuid, true);
    const { data: appsContent } = useInfiniteContent(
        {
            projectUuids: projectUuid ? [projectUuid] : [],
            spaceUuids: spaceUuid ? [spaceUuid] : [],
            contentTypes: [ContentType.DATA_APP],
            pageSize: 100,
        },
        { enabled: !!projectUuid && !!spaceUuid },
    );
    const { user } = useApp();
    const [search, setSearch] = useState<string>('');
    const visibleItems = useMemo(() => {
        const childSpaces = spaces.filter(
            (s) => s.parentSpaceUuid === spaceUuid,
        );
        const dashboardsInSpace = space?.dashboards || [];
        const chartsInSpace = space?.queries || [];
        const appsInSpace =
            appsContent?.pages.flatMap((page) =>
                page.data.map(contentToResourceViewItem),
            ) ?? [];
        const allItems = [
            ...wrapResourceView(
                childSpaces.map(spaceToResourceViewItem),
                ResourceViewItemType.SPACE,
            ),
            ...wrapResourceView(
                dashboardsInSpace,
                ResourceViewItemType.DASHBOARD,
            ),
            ...wrapResourceView(chartsInSpace, ResourceViewItemType.CHART),
            ...appsInSpace,
        ];
        if (search && search !== '') {
            const matchingItems: ResourceViewItem[] = [];
            new Fuse(allItems, {
                keys: ['data.name'],
                ignoreLocation: true,
                threshold: 0.3,
            })
                .search(search)
                .forEach((res) => matchingItems.push(res.item));
            return matchingItems;
        }
        return allItems;
    }, [space, spaces, spaceUuid, appsContent, search]);

    if (user.data?.ability?.cannot('view', 'SavedChart')) {
        return <ForbiddenPanel />;
    }

    if (isInitialLoading) {
        return <EmptyStateLoader my="xl" title="Loading space" />;
    }

    if (error) {
        return <ErrorState error={error.error} />;
    }

    if (space === undefined) {
        return (
            <div style={{ marginTop: '20px' }}>
                <SuboptimalState
                    title="Space does not exist"
                    description={`We could not find space with uuid ${spaceUuid}`}
                />
            </div>
        );
    }

    return (
        <Stack spacing="md" m="lg">
            <Group position="apart">
                <PageBreadcrumbs
                    items={[
                        {
                            title: 'Spaces',
                            to: `/projects/${projectUuid}/spaces`,
                        },
                        {
                            title: space.name,
                            active: true,
                        },
                    ]}
                />
            </Group>
            <TextInput
                icon={<MantineIcon icon={IconSearch} />}
                rightSection={
                    search ? (
                        <ActionIcon onClick={() => setSearch('')}>
                            <MantineIcon icon={IconX} />
                        </ActionIcon>
                    ) : null
                }
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <ResourceView
                items={visibleItems}
                listProps={{
                    defaultSort: { updatedAt: ResourceSortDirection.DESC },
                    defaultColumnVisibility: {
                        space: false,
                        updatedAt: false,
                        actions: false,
                    },
                }}
                emptyStateProps={{
                    icon: <IconLayoutDashboard size={30} />,
                    title: 'No items added yet',
                }}
            />
        </Stack>
    );
};

export default MobileSpace;
